/**
 * Phase 8 T-15 §14 C–D — Step 3: "Invite your teammates"
 *   /onboarding/team
 *
 * Fields: multi-email tag input + "Allow all @domain emails to join" toggle.
 * Footer: Back | Skip | Continue (Continue → /home + toast "Workspace ready.")
 */

import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Users, X } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { orgService } from '@core/supabase/services/orgService';
import { useToast } from '@shared/hooks/useToast';
import type { OnboardingOutletContext } from './OnboardingLayout';

function parseEmails(raw: string): string[] {
  return raw
    .split(/[\s,;]+/)
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s.includes('@') && s.includes('.'));
}

export function OnboardingTeamStep() {
  const { navigate } = useOutletContext<OnboardingOutletContext>();
  const { user } = useAuth();
  const { toast, showToast } = useToast();

  const [emailInput, setEmailInput] = useState('');
  const [tags, setTags]             = useState<string[]>([]);
  const [allowDomain, setAllowDomain] = useState(false);
  const [saving, setSaving] = useState(false);

  const domain = user?.email?.split('@')[1] ?? '';

  const addTagsFromInput = () => {
    const parsed = parseEmails(emailInput);
    if (!parsed.length) return;
    setTags((prev) => {
      const merged = Array.from(new Set([...prev, ...parsed]));
      return merged;
    });
    setEmailInput('');
  };

  const removeTag = (email: string) => setTags((prev) => prev.filter((e) => e !== email));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addTagsFromInput();
    }
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      /* Flush any remaining email in the input field */
      const allEmails = Array.from(new Set([...tags, ...parseEmails(emailInput)]));

      if (user?.orgId && allEmails.length) {
        /* TODO(§8-v2): call invite-email endpoint when available.
           For now, persist pending invites to localStorage so Team page can display them. */
        const key = `onb_pending_invites_${user.orgId}`;
        localStorage.setItem(key, JSON.stringify(allEmails));
        /* Suppress the unused-import lint warning */
        void orgService;
      }

      /* Allow @domain toggle — TODO(§8-v2): persist to org settings */
      if (allowDomain && domain) {
        localStorage.setItem(`onb_allow_domain_${user?.orgId ?? ''}`, domain);
      }
    } catch (_) {
      /* non-blocking */
    } finally {
      setSaving(false);
    }

    navigate('/home');
    showToast({ title: 'Workspace ready.', body: 'You\'re all set — start adding jobs and candidates.' });
  };

  const handleSkip = () => {
    navigate('/home');
    showToast({ title: 'Workspace ready.', body: 'You can invite teammates from Settings → Team anytime.' });
  };

  return (
    <div className="flex flex-col min-h-[480px]">
      {toast}

      {/* Header */}
      <div className="px-7 pt-7 pb-5 border-b border-[var(--vscode-border-primary)]">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981' }}>
            <Users className="w-4 h-4" />
          </div>
          <h2 className="text-[16px] font-bold vscode-text-primary">Invite your teammates</h2>
        </div>
        <p className="text-[12px] vscode-text-tertiary ml-11">Step 3 of 3 — Build your team</p>
      </div>

      {/* Body */}
      <div className="flex-1 px-7 py-5 space-y-4 overflow-y-auto">

        {/* Multi-email tag input */}
        <div>
          <label className="text-[12px] font-medium vscode-text-secondary block mb-1.5" htmlFor="onb-team-emails">
            Email addresses
          </label>

          {/* Tag bubbles */}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {tags.map((email) => (
                <span key={email}
                  className="inline-flex items-center gap-1 rounded-full border border-[var(--vscode-border-primary)] vscode-bg-elevated px-2.5 py-0.5 text-[11px] font-medium vscode-text-secondary">
                  {email}
                  <button type="button" onClick={() => removeTag(email)}
                    className="vscode-text-tertiary hover:vscode-text-primary transition-colors -mr-0.5">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <input
            id="onb-team-emails"
            className="input text-sm"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={addTagsFromInput}
            placeholder="recruiter@yourfirm.com, manager@yourfirm.com"
            autoFocus
          />
          <p className="text-[11px] vscode-text-tertiary mt-1">
            Press Enter or comma after each address. Invites are valid for 7 days.
          </p>
        </div>

        {/* Allow @domain toggle */}
        {domain && (
          <label className="flex items-center justify-between gap-4 rounded-[10px] border border-[var(--vscode-border-primary)] px-4 py-3 cursor-pointer hover:vscode-bg-hover transition-colors">
            <div>
              <p className="text-[13px] font-semibold vscode-text-primary">
                Allow all <span className="font-mono text-[#8B80F9]">@{domain}</span> emails to join
              </p>
              <p className="text-[11px] vscode-text-tertiary mt-0.5">
                Anyone with a <strong>@{domain}</strong> address can join your workspace without an explicit invite.
              </p>
            </div>
            <div
              role="switch"
              aria-checked={allowDomain}
              onClick={() => setAllowDomain((v) => !v)}
              className={[
                'relative w-10 h-6 rounded-full transition-colors shrink-0 cursor-pointer',
                allowDomain ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600',
              ].join(' ')}
            >
              <span
                className={[
                  'absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform',
                  allowDomain ? 'translate-x-5' : 'translate-x-1',
                ].join(' ')}
              />
            </div>
          </label>
        )}

        <p className="text-[12px] vscode-text-tertiary leading-relaxed">
          You can always invite more teammates from{' '}
          <button type="button" className="underline underline-offset-2 hover:vscode-text-secondary transition-colors"
            onClick={() => navigate('/settings/team')}>
            Settings → Team
          </button>
          .
        </p>

      </div>

      {/* Footer */}
      <div className="px-7 py-4 border-t border-[var(--vscode-border-primary)] flex items-center gap-3">
        <button type="button" onClick={() => navigate('/onboarding/workspace')}
          className="flex items-center gap-1.5 rounded-[10px] px-4 py-2.5 text-sm font-bold transition-colors"
          style={{ color: '#8B80F9', background: 'rgba(139,128,249,0.08)', border: '1.5px solid rgba(139,128,249,0.28)' }}>
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button type="button" onClick={handleSkip}
          className="text-[13px] font-semibold vscode-text-tertiary hover:vscode-text-secondary transition-colors">
          Skip
        </button>
        <div className="flex-1" />
        <button
          type="button"
          disabled={saving}
          onClick={() => void handleFinish()}
          className="flex items-center gap-2 rounded-[10px] px-5 py-2.5 text-sm font-bold text-white transition-colors disabled:opacity-60"
          style={{
            background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
            boxShadow: '0 3px 12px rgba(16,185,129,0.38)',
          }}
        >
          Finish setup <ArrowRight className="w-4 h-4" />
        </button>
      </div>

    </div>
  );
}
